<script>

	import { onMount } from 'svelte';
	import { fade } from 'svelte/transition';

	import isEmail from 'validator/es/lib/isEmail';
	import isURL from 'validator/es/lib/isURL';

	import { getServerData, postServerData } from './prelude.js';
	import { autoblur } from './autoblur.js';
	import { getErrorColor, bestContrast, isDark, colorMix } from './color.js';

	import Field from './Field.svelte';
	import BigTick from './BigTick.svelte';

	let mounted = false;

	let formWidth;
	let wide = false;
	let extraWide = false;

	let formHeight;
	let successHeight;

	let container;
	let instructions;
	let pageRef;
	let staging = false;
	let displayLang = 'EN';
	let errorColor;
	// let oopsColor;
	let boxBorderColor;

	let fields = [];
	let responses = {};
	let problems = {};

	let submitting = false;
	let submitted = false;
	let success = false;
	let confirmed = false;
	let oops = false;

	const labels = {
		'submit': {
			'EN': 'Submit'
		},
		'thanks': {
			'EN': 'Thank you.'
		},
		'confirm': {
			'EN': 'Please check your email inbox to confirm your subscription.'
		},
		'confirmed': {
			'EN': 'Your subscription has been confirmed successfully.'
		},
		'oops': {
			'EN': 'Sorry, we couldn’t confirm this subscription.'
		},
		'ok': {
			'EN': 'OK'
		}
	};

	$: wide = (formWidth > 399) ? true : false;
	$: extraWide = (formWidth > 899) ? true : false;

	function clearProblem(e) {
		problems[e.target.name] = false;
	}

	async function handleSubmit() {

		if (!responses.f) {
			problems.f = true;
		}

		if (!responses.l) {
			problems.l = true;
		}

		if (!responses.e) {
			problems.e = true;
		}

		for (const f of fields) {
			if (f.component == 'EmailField') {
				if (responses[f.ref]) {
					if (!isEmail(responses[f.ref])) {
						problems[f.ref] = true;
					}
				}
			} else if ((f.component == 'UrlField') || (f.component == 'SocialMediaUrlField')) {
				if (responses[f.ref]) {
					if (!isURL(responses[f.ref], { protocols: ['http','https'] })) {
						problems[f.ref] = true;
					} else {
						if (!/^https?\:\/\//.test(responses[f.ref])) {
							responses[f.ref] = "https://" + responses[f.ref];
						}
					}
				}
			}
		}

		if (!(Object.values(problems).some(value => value))) {

			successHeight = formHeight;

			submitting = true;

			let u = localStorage.getItem('azn_u');

			const responsesJson = JSON.stringify(responses);

			// console.log({responsesJson});

			const response = await postServerData('sites/signups/' + pageRef, {
				responses: responsesJson,
				dataStatus: staging ? 'staging' : 'published',
				u: u
			});

			if (response) {
				submitted = true;
				setTimeout(() => {
					success = true;
				}, 300);
			}

			submitting = false;

		}

	}

	$: if (successHeight) {
		setContainerHeight();
	}

	function setContainerHeight() {
		let styles = container.getAttribute('style');
		if (styles) {
			const regex = new RegExp(`--successHeight:.+?;`);
			styles = styles.replace(regex, '--successHeight:' + successHeight + ';');
			container.setAttribute('style', styles);
		} else {
			container.setAttribute('style', '--successHeight:' + successHeight + ';');
		}
	}

	function closeDialog() {
		confirmed = false;
		oops = false;
	}

	onMount(async () => {

		container = document.getElementById('aznsignup');

		instructions = container.removeChild(document.getElementById('aznsignup').firstElementChild);

		pageRef = container.dataset.page;
		staging = container.dataset.staging ? true : false;

		if (!pageRef) {
			pageRef = document.body.dataset.page;
		}

		let hash = location.hash;
		let hashItems = hash.split('/');

		if ((hashItems[1] == 'azn') && (hashItems[2] == 's') && (hashItems[3])) {

			mounted = true;

			let u = localStorage.getItem('azn_u');

			const response = await postServerData('sites/signups/confirm/' + pageRef, {
				token: hashItems[3],
				u: u
			});

			if (response && response.status && response.status == 'success') {
				confirmed = true;
			} else {
				oops = true;
			}

		}

		let fieldList = container.dataset.fields;

		if (!fieldList) {
			fieldList = "f,l,e";
		}

		fields = await getServerData('sites/signups/fields/' + pageRef, {
			fields: fieldList
		});

		// console.log({fields});

		mounted = true;

		const themeAccent1 = getColorFromStyles(document.body, 'accent1');
		const themeAccent2 = getColorFromStyles(document.body, 'accent2');
		const themeAccent3 = getColorFromStyles(document.body, 'accent3');
		const themeTextColor = getColorFromStyles(document.body, 'textColor');
		const themeBgColor = getColorFromStyles(document.body, 'bgColor');

		const red = getErrorColor([themeAccent1, themeAccent2, themeAccent3, themeTextColor, themeBgColor]);

		// console.log('red', red);

		const outline = container.closest('.component').classList.contains('outline');
		const fieldBgColor = getColorFromStyles(container.closest('.component'), outline ? 'bgColor' : 'textColor');
		const fieldFgColor = getColorFromStyles(container.closest('.component'), outline ? 'textColor' : 'bgColor');

		// console.log('outline',outline,'fieldBgColor',fieldBgColor,'fieldFgColor',fieldFgColor);

		errorColor = bestContrast(fieldBgColor, red, fieldFgColor);

		// console.log('errorColor',errorColor);

		// const oopsBgColor = outline ? fieldBgColor : fieldFgColor;
		// const oopsFgColor = outline ? fieldFgColor : fieldBgColor;

		// oopsColor = bestContrast(oopsBgColor, red, oopsFgColor);

		let boxBorderColorMix = 80;

		if (isDark(fieldBgColor)) {
			boxBorderColorMix = 40;
		}

		boxBorderColor = colorMix(fieldBgColor, fieldFgColor, boxBorderColorMix);

	});

	function getColorFromStyles(elem, property) {
		const regex = new RegExp(`--${property}:(.+?);`);
		const match = regex.exec(elem.getAttribute('style'));
		return match ? match[1] : undefined;
	}

</script>

<style>
	:global(#aznsignup) {
		min-height: calc(1px * var(--successHeight));
	}
	form {
		display: flex;
		flex-wrap: wrap;
		width: 100%;
	}
	form > div {
		flex: 0 0 100%;
		font-size: 1em;
		font-family: var(--textFont, sans-serif);
		color: var(--textColor, #333);
		padding: 0.5em 1em 0.5em 0;
		box-sizing: border-box;
	}
	:global(.maxed) form .signup_content {
		display: none;
	}
	:global(.contentless) form .signup_content :global(*) {
		display: none;
	}
	form .signup_form {
		flex: 0 0 100%;
		padding: 0;
	}
	form .signup_form > div {
		display: flex;
		flex-wrap: wrap;
		margin: 0 -0.5em;
	}

	@media screen and (min-width:800px) {
		form {
			flex-wrap: nowrap;
		}
		form > div {
			flex: 0 0 calc(1% * var(--ratio, 50));
		}
		form .signup_content {
			display: flex;
			align-items: var(--verticalAlignment, flex-start);
		}
		form .signup_form {
			flex: 0 0 calc(100% - (1% * var(--ratio, 50)));
			align-self: var(--verticalAlignment, flex-start);
			padding-left: 1rem;
		}
		:global(.maxed) form .signup_form {
			flex-basis: 100%;
		}
	}

	.signup_form :global(.field) {
		flex: 1 1 calc(100% - 1em);
		position: relative;
		padding: 0;
		margin: 0.5em;
		box-sizing: border-box;
	}
	:global(.linear) .signup_form :global(.field:first-child) {
		margin-top: 0.25rem;
	}
	:global(.compact) .signup_form :global(.field) {
		background: var(--fieldColor, var(--textColor, #333));
	}
	:global(.compact) .signup_form :global(.field:not(.RadioButtons):not(.CheckboxGroup)) {
		height: 2em;
		height: calc(1em + clamp(16px, 1em, 1em));
	}
	:global(.linear) .signup_form :global(.textual),
	:global(.linear) .signup_form :global(.Select),
	:global(.linear.solid) .signup_form :global(.radiobuttons),
	:global(.linear.solid) .signup_form :global(.checkboxes) {
		background: var(--fieldColor, var(--textColor, #333));
	}

	:global(.outline.compact) .signup_form :global(.field) {
		background: transparent;
		border: 1px solid var(--boxBorderColor, var(--textColor, #333));
	}
	:global(.outline.compact) .signup_form :global(.field:before) {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		opacity: 0.9;
		background: var(--bgColor, #eee);
	}
	:global(.outline.linear) .signup_form :global(input),
	:global(.outline.linear) .signup_form :global(.Select) {
		position: relative;
		background: var(--bgColor, #eee);
		border: 1px solid var(--boxBorderColor, var(--textColor, #333));
	}

	.signup_form div.button,
	:global(.outline) .signup_form div.button {
		border: 0;
		background: transparent;
	}

	:global(.compact) .signup_form.wide :global(.field.f),
	:global(.compact) .signup_form.wide :global(.field.l) {
		flex: 1 1 calc(50% - 1em);
	}
	:global(.compact) .signup_form.wide > div > :global(div:nth-last-child(2)) {
		flex: 1 1 auto;
	}
	:global(.compact) .signup_form.wide > div > :global(div.CheckboxGroup),
	:global(.compact) .signup_form.wide > div > :global(div.RadioButtons) {
		flex: 1 1 calc(100% - 1em);
	}
	.signup_form.wide div.button {
		/*flex: 0 0 12ch;*/
		flex: 0 0 7em;
	}

	form :global(.field .fieldlabel),
	form :global(.field .textual),
	form :global(.field .Select),
	form :global(.field button) {
		display: block;
		width: 100%;
		box-sizing: border-box;
		padding: 0.5em 1em;
		background: transparent;
		font-size: 1em;
		font-size: 0.875rem;
		font-family: var(--textFont, sans-serif);
		line-height: 1em;
		vertical-align: middle;
		border: 0;
		margin: 0;
		/*color: var(--textColor, #333);*/
		color: var(--fieldTextColor, var(--bgColor, #fff));
	}
	@media all and (max-width:800px) {
		form :global(.field .fieldlabel),
		form :global(.field .textual),
		form :global(.field .Select),
		form :global(.field button) {
			font-size: clamp(16px, 1em, 1em);
		}
	}
	form :global(.field .Select) {
		position: relative;
	}
	form :global(.field .Select button) {
		padding: 0;
		text-align: left;
	}
	:global(.compact) form :global(.field .Select button) {
		padding: 0 1rem;
	}
	form :global(.field .Select button span) {
		display: flex;
		min-height: calc(1em + 2px);
		align-items: center;
	}
	:global(.compact) form :global(.field:not(.RadioButtons):not(.CheckboxGroup) .fieldlabel),
	:global(.compact) form :global(.field .textual),
	:global(.compact) form :global(.field .Select),
	:global(.compact) form :global(.field button) {
		position: absolute;
		top: 0;
		left: 0;
		bottom: 0;
	}
	:global(.linear) form :global(.field .fieldlabel) {
		padding-left: 0;
		font-size: 0.875rem;
		color: var(--textColor, #333);
		font-weight: 600;
	}
	:global(.outline) form :global(.field .fieldlabel),
	:global(.outline) form :global(.field .textual),
	:global(.outline) form :global(.field .Select),
	:global(.outline) form :global(.field button) {
		color: var(--textColor, #333);
	}
	form :global(.field.problem input) {
		padding-right: 2.4em;
	}
	:global(.compact) form :global(.field .fieldlabel) {
		opacity: 0.8;
		transition: opacity 0.2s ease-out;
	}
	:global(.compact) form :global(.field:not(.RadioButtons):not(.CheckboxGroup) .fieldlabel.hasContent) {
		opacity: 0;
	}
	form :global(.field input:focus) {
		outline: none;
	}
	form .field button,
	:global(.outline) form .field button {
		border: 1px solid transparent;
		background: var(--linkColor, #333);
		color: var(--buttonTextColor, #eee);
	}
	form :global(.field svg) {
		position: absolute;
		top: 0.4em;
		right: 0.5em;
		width: 1.2em;
		height: 1.2em;
		animation: pulse 0.3s ease-out both;
	}
	form svg path {
		fill: var(--errorColor, var(--bgColor, #fff));
	}
	form :global(.Select svg path) {
		fill: var(--fieldTextColor);
	}
	:global(.outline) form :global(.Select svg path) {
		fill: var(--textColor);
	}
	@keyframes pulse {
	  0% {
	    transform: scale(1);
	  }
	  40% {
	    transform: scale(1.2);
	  }
	  100% {
	    transform: scale(1);
	  }
	}
	.success {
		display: grid;
		place-content: center;
	}
	.success > div {
		display: flex;
		align-items: center;
	}
	.success p {
		padding-left: 1em;
		font-size: 1em;
		font-family: var(--textFont, sans-serif);
		color: var(--textColor, #333);
	}
	.confirmbox {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: 90000;
		display: grid;
		place-content: center;
	}
	.confirmbox:before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: var(--textColor, #333);
		opacity: 0.95;
	}
	.confirmbox > div {
		position: relative;
		background: var(--bgColor, #eee);
		box-shadow: 0 4px 12px -2px rgba(0,0,0,0.6);
		padding: 2em;
		text-align: center;
		font-size: 1em;
		font-family: var(--textFont, sans-serif);
		color: var(--textColor, #333);
		max-width: 90vw;
		box-sizing: border-box;
	}
	.confirmbox p {
		margin: 2em 0 0 0;
	}
	.confirmbox button {
		background: var(--linkColor, #333);
		color: var(--buttonTextColor, #eee);
		box-sizing: border-box;
		padding: 0.5em 1em;
		line-height: 1em;
		vertical-align: middle;
		border: 1px solid var(--linkColor, #333);
		margin: 0;
		font-size: 1em;
	}
	form button:hover,
	form button:focus,
	.confirmbox button:hover,
	.confirmbox button:focus {
		outline: none;
		background: var(--bgColor, #fff);
		border: 1px solid var(--textColor, #333);
		color: var(--textColor, #333);
	}
	.confirmbox .oops svg {
		width: 3em;
		height: 3em;
	}
	.confirmbox .oops svg path {
		fill: var(--errorColor, currentColor);
	}
</style>

{#if mounted}

	{#if success}
		<div class="success" transition:fade|local={{duration:200}} style="min-height:{successHeight}px">
			<div>
				<BigTick/>
				<p>
					<strong>{labels.thanks[displayLang]}</strong>
					<span>{labels.confirm[displayLang]}</span>
				</p>
			</div>
		</div>
	{/if}
	{#if !submitted}
		<form on:submit|preventDefault={handleSubmit} transition:fade|local={{duration:200}} bind:clientHeight={formHeight} disabled={submitting} style="--errorColor:{errorColor};--boxBorderColor:{boxBorderColor};">
			<div class="signup_content">
				{@html instructions.outerHTML}
			</div>
			<div bind:clientWidth={formWidth} class:wide class:extraWide class="signup_form">
				<div>

					{#each fields as field}
						<Field
							{field}
							bind:responses
							bind:problems
						/>
					{/each}

					<div class="field button">
						<button type="submit" use:autoblur>
							{labels.submit[displayLang]}
						</button>
					</div>

				</div>
			</div>
		</form>
	{/if}

	{#if confirmed}
		<div class="confirmbox" on:click|stopPropagation={closeDialog} in:fade={{delay:400,duration:600}} out:fade={{duration:200}}>
			<div>
				<BigTick big={true}/>
				<p>
					<strong>{labels.thanks[displayLang]}</strong>
					<span>{labels.confirmed[displayLang]}</span>
				</p>
				<p>
					<button class="signup_submit" type="button" on:click|stopPropagation={closeDialog} use:autoblur>
						{labels.ok[displayLang]}
					</button>
				</p>
			</div>
		</div>
	{/if}

	{#if oops}
		<div class="confirmbox" on:click|stopPropagation={closeDialog} in:fade={{duration:400}} out:fade={{duration:200}} style="--errorColor:{errorColor};">
			<div>
				<p class="oops">
					<svg viewBox="0 0 56 56"><path d="M56 28c0 15.464-12.536 28-28 28s-28-12.536-28-28 12.536-28 28-28 28 12.536 28 28zm-28 19.89c2.455 0 4.583-2.053 4.603-4.604-.02-2.513-2.148-4.564-4.603-4.564-2.532 0-4.623 2.052-4.604 4.564-.02 2.551 2.071 4.604 4.604 4.604zm4.257-39.78h-8.535l.768 26.505h7.019l.748-26.505z"/></svg>
				</p>
				<p>
					<span>{labels.oops[displayLang]}</span>
				</p>
				<p>
					<button type="button" on:click|stopPropagation={closeDialog} use:autoblur>
						{labels.ok[displayLang]}
					</button>
				</p>
			</div>
		</div>
	{/if}

{/if}
